import { useFlowBuilder } from 'hooks/useFlowBuilder';
import React, { createContext, FC, PropsWithChildren, useContext, useState } from 'react';
import { useNotification } from '@wix/gg-components';
import { OrchestratorProvider, StepDef } from '@wix/gg-common-lib/orchestrator';
import { extractUrlParam } from '@wix/gg-common-lib/utils';
import { ActionId, StepId } from 'config/flow';
import { useAppStore } from 'stores/state';
import { TARGET_STEP_PARAM } from 'config/consts';
import { StepParams } from 'types/steps';
import { biEvents } from 'api/bi-events';
import { BI_STEP_NAME } from 'config/bi-events';

type Value = {
  steps: StepDef<StepParams>[];
};

const Context = createContext<Value>({} as Value);

export const useFlowContext = () => useContext(Context);

export const FlowContextProvider: FC<PropsWithChildren> = ({ children }) => {
  const [targetStepId] = useState(() => extractUrlParam<StepId | ActionId>(TARGET_STEP_PARAM));
  const { currentStep } = useAppStore.getState();
  const flow = useFlowBuilder();
  const { promptNotification } = useNotification();

  const targetStep = targetStepId || currentStep;
  const steps = flow.filter((step) => step.type === 'step');
  const initialIndex = flow.findIndex((step) => step.id === targetStep);

  return (
    <Context.Provider value={{ steps }}>
      <OrchestratorProvider
        onError={(error) => promptNotification({ message: error, status: 'error' })}
        initialIndex={initialIndex >= 0 ? initialIndex : 0}
        flow={flow}
        onStepChange={(step) => {
          if (step?.id) {
            useAppStore.setState({ currentStep: step.id as StepId });
            biEvents.stepViewed({ pageName: BI_STEP_NAME[step.id as StepId] });
          }
        }}
      >
        {children}
      </OrchestratorProvider>
    </Context.Provider>
  );
};
